import { useMemo, useRef } from "react";
import { useResizeDetector } from "react-resize-detector";
import { selectTenantId } from "../../../../../util/store/selectors";
import { getStore, useSelector } from "../../../../../util/store/store";
import { CONTRACT_TYPE } from "../../../../../util/varibles/constants";
import { IAvailableTime, IContract, IVessel } from "../../../../../util/varibles/interface";
import { IVesselHeight } from "../../../../OverviewPage/Calendar/Vessels";
import Menu from "../../../_components/Menu";
import { selectPlan } from "../../../constants";
import styles from '../style.module.scss';
import AvailableTimes from "./AvaiableTimes";
import Contracts from "./Contracts";
import Layout from "./Layout";
import Loading from "./Loading";
import Month from "./Month";
import Operations from "./Operations";
import Vessel from "./Vessel";
import Weeks from "./Weeks";

export const HEIGHT_PER_OP = 38;
interface IVesselReduceResult {
    list: (IVessel & { isPermission: boolean })[];
    contracts: IContract[];
    available_times: IAvailableTime[];
    totalHeight: number;
    vesselHeight: IVesselHeight;
}

export default function Vessels() {
    const vessels = useSelector(selectPlan.vessels);
    const screenRef = useRef<HTMLDivElement>(null);
    const heightRef = useRef(0);
    const vesselHeightRef = useRef<IVesselHeight>({})

    const { list, contracts, available_times, totalHeight } = useMemo(() => {
        const { vesselHeight, ...args } = vessels.reduce((rs: IVesselReduceResult, item) => {
            const { id, contracts, available_times, isOwn, permission, isShow, row = 1 } = item;
            const isPermission = !!(isOwn || permission);
            if (isShow) {
                const vesselIndex = rs.list.length;
                const height = Math.max(85, ((row + 1) * HEIGHT_PER_OP + ((row + 1) * HEIGHT_PER_OP * .08) + 5));

                rs.vesselHeight[id] = {
                    index: rs.totalHeight,
                    height,
                    position: vesselIndex,
                };

                rs.totalHeight += height;

                rs.list.push({ ...item, isPermission });

                available_times.forEach((sub: any) => {
                    rs.available_times = [...rs.available_times, { ...sub, vesselId: id, isPermission, vesselIndex }];
                })

                const userTenantId = selectTenantId(getStore());

                contracts.forEach((sub: any) => {
                    const { type, tenant_id } = sub;
                    if (type === CONTRACT_TYPE.FIXED) {
                        rs.contracts = [...rs.contracts, {
                            ...sub,
                            isOwn: tenant_id === userTenantId,
                            vesselId: id,
                            isPermission,
                            vesselIndex
                        }];
                    }
                })
            }
            return rs;
        }, { list: [], contracts: [], available_times: [], totalHeight: 0, vesselHeight: {} });
        vesselHeightRef.current = vesselHeight;
        if (screenRef.current) {
            const piece = Math.max(0, heightRef.current - args.totalHeight - 45);
            const moreHeight = args.list.length ? piece / args.list.length : 0;
            screenRef.current?.style.setProperty('--more-height', `${moreHeight}px`);
            Object.keys(vesselHeight).forEach(key => {
                const { height, index: vesselIndex, position } = vesselHeight[key];
                screenRef.current?.style.setProperty('--' + key, `${height}px`);
                screenRef.current?.style.setProperty('--' + key + '-top', `calc(${vesselIndex + 60}px + (${position} * var(--more-height)))`);
            })
        }
        return args;
    }, [vessels])

    const count = list.length;
    useResizeDetector({
        targetRef: screenRef,
        onResize: (_, h) => {
            if (!screenRef.current) return;
            if (h) {
                heightRef.current = h;
                const piece = Math.max(0, heightRef.current - totalHeight - 45);
                screenRef.current.style.setProperty('--more-height', `${count ? piece / count : 0}px`);
                Object.keys(vesselHeightRef.current).forEach(key => {
                    const { height, index: vesselIndex, position } = vesselHeightRef.current[key];
                    screenRef.current?.style.setProperty('--' + key, `${height}px`);
                    screenRef.current?.style.setProperty('--' + key + '-top', `calc(${vesselIndex + 65}px + (${position} * var(--more-height)))`);
                })
            }
        }
    });

    return <div ref={screenRef} className={styles.screen} data-empty={vessels.length === 0} >
        <div className={styles.schedule}>
            <div className={styles.vessels} data-max={count}>
                <Month />
                {list.map((item: any) => <Vessel
                    key={item.id}
                    data={item}
                />)}
            </div>

            <Menu>
                <>
                    <AvailableTimes source={available_times} vessels={list} />
                    <Contracts source={contracts} vessels={list} />
                    <Operations vessels={list} />
                    <Weeks vessels={list} />
                </>
            </Menu>
            <Layout vessels={list} />
        </div>
        <Loading />
    </div>
}
